import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from 'react';
import debounce from 'lodash/debounce';

import { IndexName } from '@enums/IndexName';
import useFilterDefsQuery from '@hooks/queryHooks/useFilterDefsQuery';
import { OrderBy } from '@models/OrderBy';
import { TOrderProvidersBy } from '@models/OrderProvidersBy';
import { FilterGroup } from '@models/SearchFilter';
import { SearchQuery } from '@models/SearchQuery';
import { DEFAULT_CHUNK_SIZE__SEARCH } from '@utils/chunkSize';
import { getNewFilterGroup } from '@utils/filterUtils';
import { CompatibilityMap } from '@utils/searchUtils';

import SearchAndFilterContext, { SearchAndFilterValues } from './context';
import useTitleSearch from './useTitleSearch';
import { getFilterCount, stripFilterGroup } from './utils';

export type SearchAndFilterProviderProps = Pick<SearchAndFilterValues, 'isReadonly'> & {
  disableSearch?: boolean;
  pageSize?: number;
  provider?: string;
  searchQuery?: Partial<SearchQuery>;
};

const initFilters = getNewFilterGroup(true);

const emptySearchQuery: SearchQuery = {
  distinctBy: '',
  filter: initFilters,
  indexName: IndexName.Assets,
  orderBy: [],
  queryText: '',
};

const SearchAndFilterProvider: FC<PropsWithChildren<SearchAndFilterProviderProps>> = ({
  children,
  disableSearch,
  isReadonly,
  pageSize = DEFAULT_CHUNK_SIZE__SEARCH,
  provider: initProvider,
  searchQuery: initSearchQuery = {},
}) => {
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    ...emptySearchQuery,
    ...initSearchQuery,
  });
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const [pageNo, setPageNo] = useState(1);
  const [provider, setProvider] = useState(initProvider);

  const { filter, indexName } = deferredSearchQuery;

  const filterDefLoad = useFilterDefsQuery(indexName);

  const setOrderBy: Dispatch<SetStateAction<OrderBy[]>> = useCallback(
    arg =>
      setSearchQuery(prev => ({ ...prev, orderBy: Array.isArray(arg) ? arg : arg(prev.orderBy) })),
    []
  );

  const setOrderProvidersBy: Dispatch<SetStateAction<TOrderProvidersBy | undefined>> = useCallback(
    arg =>
      setSearchQuery(prev => {
        const tmp = {
          ...prev,
          orderProvidersBy: typeof arg === 'function' ? arg(prev.orderProvidersBy) : arg,
        };

        if (!Object.values(tmp.orderProvidersBy || {}).some(Boolean)) delete tmp.orderProvidersBy;
        return tmp;
      }),
    []
  );

  const filterTrigger = JSON.stringify(filter);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filterCount = useMemo(() => getFilterCount(filter), [filterTrigger]);

  const updateFilter = useCallback(
    (newFilter: FilterGroup, newQueryText?: string) =>
      setSearchQuery(prev => ({
        ...prev,
        filter: stripFilterGroup(newFilter) ?? initFilters,
        ...(newQueryText == null ? {} : { queryText: newQueryText }),
      })),
    []
  );

  const updateIndexName = useCallback(
    (indexName: IndexName, compatibility: CompatibilityMap) =>
      setSearchQuery(prev =>
        Object.entries(compatibility).reduce(
          (acc, [key, compatible]) => ({
            ...acc,
            [key]: compatible
              ? prev[key as keyof SearchQuery]
              : emptySearchQuery[key as keyof SearchQuery],
          }),
          { ...prev, indexName }
        )
      ),
    []
  );

  const debouncedUpdateQueryText = useMemo(
    () => debounce((queryText: string) => setSearchQuery(prev => ({ ...prev, queryText })), 300),
    []
  );

  const updateDistinctBy = useCallback(
    (distinctBy?: string) => setSearchQuery(prev => ({ ...prev, distinctBy })),
    []
  );

  const values = useMemo<Omit<SearchAndFilterValues, 'queryResult'>>(
    () => ({
      filterCount,
      filterDefLoad,
      filterTrigger,
      isReadonly,
      pageNo,
      pageSize,
      provider,
      searchQuery: deferredSearchQuery,
      setOrderBy,
      setOrderProvidersBy,
      setPageNo,
      updateDistinctBy,
      updateFilter,
      updateIndexName,
      updateProvider: setProvider,
      updateQueryText: debouncedUpdateQueryText,
      updateSearchQuery: setSearchQuery,
    }),
    [
      filterCount,
      filterDefLoad,
      filterTrigger,
      isReadonly,
      pageNo,
      pageSize,
      provider,
      deferredSearchQuery,
      setOrderBy,
      setOrderProvidersBy,
      setPageNo,
      updateDistinctBy,
      updateFilter,
      updateIndexName,
      debouncedUpdateQueryText,
    ]
  );

  const queryResult = useTitleSearch(values, disableSearch);

  const valuesWithQueryResult = useMemo<SearchAndFilterValues>(
    () => ({ ...values, queryResult }),
    [values, queryResult]
  );
  return (
    <SearchAndFilterContext.Provider value={valuesWithQueryResult}>
      {children}
    </SearchAndFilterContext.Provider>
  );
};

export default SearchAndFilterProvider;
